<script>
import { mapState } from 'vuex';
import { contextForRoute, interpolate } from '@utils/smartPromptsContext';

export default {
  name: 'SmartPromptsPanel',

  data() {
    return {
      copiedSlug: null,
    };
  },

  computed: {
    ...mapState({
      open: state => state.ui.smartPromptsOpen,
      athleteTab: state => state.ui.athleteTab,
    }),

    context() {
      return contextForRoute(this.$route, { athleteTab: this.athleteTab });
    },

    prompts() {
      const key = `pages.smartPrompts.contexts.${this.context.kind}.items`;
      const items = this.$t(key);
      return Array.isArray(items) ? items : [];
    },

    contextLabel() {
      const key = `pages.smartPrompts.contexts.${this.context.kind}.label`;
      const translated = this.$t(key);
      return translated === key ? '' : translated;
    },
  },

  watch: {
    $route() {
      if (this.open) this.close();
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    handleKeydown(e) {
      if (e.key === 'Escape' && this.open) this.close();
    },

    toggle() {
      this.$store.commit('ui/toggleSmartPrompts');
    },

    close() {
      this.$store.commit('ui/setSmartPromptsOpen', false);
    },

    // What the dietitian reads in the panel: the clean instruction only.
    displayBody(prompt) {
      return interpolate(prompt.body, this.context.variables);
    },

    // What lands on the clipboard: optional role preamble + the instruction +
    // a context footer that re-adds the Athlisis reference and client ID we
    // hide from the preview. Composed here so the preview stays readable.
    copyText(prompt) {
      const parts = [];
      if (prompt.preamble) parts.push(prompt.preamble);
      parts.push(prompt.body);
      const footerKey = this.context.variables.athleteId
        ? 'pages.smartPrompts.copyContextClient'
        : 'pages.smartPrompts.copyContext';
      parts.push(this.$t(footerKey));
      return interpolate(parts.join('\n\n'), this.context.variables);
    },

    copyPrompt(prompt) {
      navigator.clipboard.writeText(this.copyText(prompt));
      this.copiedSlug = prompt.slug;
      setTimeout(() => {
        if (this.copiedSlug === prompt.slug) this.copiedSlug = null;
      }, 2000);
    },
  },
};
</script>

<template>
  <div class="sp-root">
    <button
      v-show="!open && prompts.length > 0"
      v-b-tooltip.left="$t('pages.smartPrompts.triggerTooltip')"
      type="button"
      class="sp-fab"
      :aria-label="$t('pages.smartPrompts.triggerTooltip')"
      @click="toggle"
    >
      <i class="fe-zap" />
    </button>

    <transition name="sp-backdrop">
      <div
        v-if="open"
        class="sp-backdrop"
        @click="close"
      />
    </transition>

    <aside
      class="sp-panel"
      :class="{ 'sp-panel--open': open }"
      :aria-hidden="!open"
      role="complementary"
    >
      <header class="modal-header sp-panel__header">
        <div class="sp-panel__title-wrap">
          <span class="sp-panel__title-badge" aria-hidden="true">
            <i class="fe-zap" />
          </span>
          <div class="sp-panel__title-text">
            <div class="sp-panel__title">{{ $t('pages.smartPrompts.title') }}</div>
            <div v-if="contextLabel" class="sp-panel__context">{{ contextLabel }}</div>
          </div>
        </div>
        <button
          type="button"
          class="close"
          :aria-label="$t('close')"
          @click="close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </header>

      <div class="sp-panel__body">
        <p class="sp-panel__hint">{{ $t('pages.smartPrompts.hint') }}</p>

        <empty-state v-if="prompts.length === 0" compact icon="fe-zap" :title="$t('pages.smartPrompts.empty')" :subtitle="$t('pages.smartPrompts.emptyText')"/>

        <ul v-else class="sp-list">
          <li v-for="prompt in prompts" :key="prompt.slug" class="sp-prompt">
            <div class="sp-prompt__title">{{ prompt.title }}</div>
            <div class="sp-prompt__body">{{ displayBody(prompt) }}</div>
            <div class="sp-prompt__actions">
              <b-button variant="link" size="sm" class="sp-prompt__copy" @click="copyPrompt(prompt)">
                <i class="fe-copy tw-mr-1" />
                {{ copiedSlug === prompt.slug
                  ? $t('pages.smartPrompts.copied')
                  : $t('pages.smartPrompts.copy') }}
              </b-button>
            </div>
          </li>
        </ul>
      </div>
    </aside>
  </div>
</template>

<style lang="scss" scoped>
$sp-width: 380px;
$sp-easing: cubic-bezier(0.22, 1, 0.36, 1);
$sp-duration: 360ms;

// FAB sits just above the prolipsis widget (bottom: 100px, 40x40).
.sp-fab {
  position: fixed;
  right: 24px;
  bottom: 152px;
  z-index: 1035;

  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%;
  background: $color_primary;
  color: #fff;
  border: 1.5px solid rgba(31, 41, 55, 0.15);
  box-shadow: 0 4px 12px rgba($color_primary, 0.28);
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease;

  i { font-size: 1rem; }

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 18px rgba($color_primary, 0.38);
  }

  &:focus-visible {
    outline: 2px solid rgba($color_primary, 0.45);
    outline-offset: 2px;
  }
}

.sp-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.22);
  z-index: 1040;
}

.sp-backdrop-enter-active,
.sp-backdrop-leave-active {
  transition: opacity $sp-duration $sp-easing;
}
.sp-backdrop-enter,
.sp-backdrop-leave-to {
  opacity: 0;
}

.sp-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: $sp-width;
  max-width: 100vw;
  background: #fff;
  box-shadow: -12px 0 32px rgba(15, 23, 42, 0.1);
  border-left: 1px solid $color_border;
  transform: translateX(100%);
  transition: transform $sp-duration $sp-easing;
  z-index: 1050;
  display: flex;
  flex-direction: column;

  &--open {
    transform: translateX(0);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    // Override global .modal-header to match panel density
    padding: 0.9rem 1rem;
    border-bottom: 1px solid $color_border;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    min-width: 0;
  }

  &__title-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: $color_primary;
    color: #fff;
    flex-shrink: 0;

    i { font-size: 0.9rem; }
  }

  &__title-text { min-width: 0; }

  &__title {
    font-size: 0.95rem;
    font-weight: 700;
    color: $color_text;
    line-height: 1.2;
  }

  &__context {
    font-size: 0.7rem;
    font-weight: 600;
    color: $color_primary;
    margin-top: 0.15rem;
    letter-spacing: 0.02em;
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 0.9rem 1rem 1.1rem;
  }

  &__hint {
    font-size: 0.8rem;
    color: #6b7280;
    margin: 0 0 0.7rem;
    line-height: 1.5;
  }

  &__empty {
    text-align: center;
    color: #6b7280;
    padding: 2rem 0;
    font-size: 0.85rem;
  }
}

.sp-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sp-prompt {
  border: 1px solid $color_border;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  background: #fafbfc;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: rgba($color_primary, 0.4);
    background: #fff;
  }

  &__title {
    font-size: 0.85rem;
    font-weight: 700;
    color: $color_text;
    margin-bottom: 0.3rem;
  }

  &__body {
    font-size: 0.78rem;
    color: #475569;
    line-height: 1.5;
    margin-bottom: 0.45rem;
    white-space: pre-wrap;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__copy {
    padding: 0.15rem 0.4rem;
    font-size: 0.75rem;
    font-weight: 600;
  }
}
</style>
